import React from "react";
import CountUp from "./CountUp";

/**
 * Metric — the big-number + small-unit pattern.
 *
 *   <Metric value={1012} unit="MB" size="lg" />
 *
 * Renders:
 *   1012   (big, Manrope 600, tabular-nums, count-up animated)
 *   MB     (small, uppercase, faded)
 *
 * Sizes map to the CSS utility classes defined in index.css:
 *   sm  -> .mb-metric-sm   (24px)
 *   md  -> .mb-metric      (32px) -- default
 *   lg  -> .mb-metric-lg   (36px)
 */
export default function Metric({
    value,
    unit = "",
    size = "md",
    decimals,
    duration = 200,
    align = "left",
    animated = true,
    className = "",
    valueClassName = "",
    unitClassName = "",
    "data-testid": testId,
}) {
    const sizeClass =
        size === "sm" ? "mb-metric-sm" :
        size === "lg" ? "mb-metric-lg" :
        "mb-metric";
    const wrap =
        align === "right" ? "items-end" :
        align === "center" ? "items-center" :
        "items-start";

    return (
        <div className={["flex flex-col leading-none", wrap, className].join(" ")} data-testid={testId}>
            <div className={[sizeClass, valueClassName].join(" ")}>
                {animated
                    ? <CountUp value={value} decimals={decimals} duration={duration} />
                    : (Number.isFinite(+value) ? (+value).toFixed(decimals ?? 0) : value)}
            </div>
            {unit && (
                <div className={["mb-metric-unit mt-1", unitClassName].join(" ")}>{unit}</div>
            )}
        </div>
    );
}

/**
 * MetricInline — same value/unit but laid out on one line (baseline-aligned).
 * For compact KPI labels where the stacked layout feels too tall.
 */
export function MetricInline({
    value,
    unit = "",
    size = "md",
    decimals,
    duration = 200,
    animated = true,
    className = "",
    valueClassName = "",
    unitClassName = "",
    "data-testid": testId,
}) {
    const sizeClass =
        size === "sm" ? "mb-metric-sm" :
        size === "lg" ? "mb-metric-lg" :
        "mb-metric";

    return (
        <div className={["flex items-baseline gap-1.5", className].join(" ")} data-testid={testId}>
            <span className={[sizeClass, valueClassName].join(" ")}>
                {animated
                    ? <CountUp value={value} decimals={decimals} duration={duration} />
                    : (Number.isFinite(+value) ? (+value).toFixed(decimals ?? 0) : value)}
            </span>
            {unit && <span className={["mb-metric-unit", unitClassName].join(" ")}>{unit}</span>}
        </div>
    );
}
